import { ThemeEvents, CartUpdateEvent, CartErrorEvent, nextCartMutation } from '@theme/events';
import { cartItemsSectionIds } from '@theme/utilities';

/**
 * Cart gift guard — a lightweight, always-on safety net that runs regardless of
 * whether the gift-progress feature is enabled. It exists to stop a cart that
 * contains ONLY gift items (or is empty) from reaching the Yampi checkout, and
 * to sweep out gifts that got stuck from an older session (or after the feature
 * was turned off).
 *
 * Scope, intentionally: this defends against the common cases — a race between
 * removing the last paid item and clicking checkout, a stale gift left in an old
 * session, or the feature being disabled while a gift lingers. It is NOT a
 * defense against an attacker manipulating the page from the console; a real
 * fraud guard must live server-side (Yampi/Dooki rejecting a gift-only payload).
 */

const SOURCE_ID = 'cart-gift-guard';
// Per-unit price (cents) at/below which an item is treated as a gift. Mirrors
// GIFT_PRICE_CEILING_CENTS in assets/cart-gifts-progress.js and the Cosmos
// GIFT_PRICE_THRESHOLD contract (R$0,02).
const GIFT_PRICE_CEILING_CENTS = 2;

// Checkout triggers we must be able to intercept: native Shopify button/anchor
// plus the mirror buttons YampiSnippet.liquid injects on the cart page/drawer.
const CHECKOUT_SELECTOR =
  '#checkout, [name="checkout"], .cart__checkout-button, .ymp_check_mainBtn, .yampi-btn-finalize-single, a.btn-checkout[href="/checkout"]';

/**
 * @param {any} item
 * @returns {boolean}
 */
function isGiftItem(item) {
  if (!item) return false;
  if (item.properties?._gobeautexCartGift) return true;
  if (typeof item.final_price === 'number' && item.final_price <= GIFT_PRICE_CEILING_CENTS) return true;
  return false;
}

/**
 * @param {{ items?: any[] }} cart
 * @returns {boolean} true when the cart has items and every one is a gift.
 */
function hasOnlyGifts(cart) {
  if (!cart?.items?.length) return false;
  return cart.items.every(isGiftItem);
}

class CartGiftGuard {
  /** @type {{ items?: any[] } | null} Latest known cart snapshot. */ #cart = null;
  /** @type {boolean} */ #sweeping = false;

  init() {
    // Keep a fresh snapshot so the click handler can decide synchronously.
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    // Capture phase so we run before Yampi's own click listeners / navigation.
    document.addEventListener('click', this.#onCheckoutClick, true);

    // Boot sweep: clear gifts stuck from an old session / disabled feature.
    this.#refreshCart().then(() => this.#sweepStuckGifts());
  }

  /** @param {CustomEvent} event */
  #onCartUpdate = (event) => {
    const cart = event?.detail?.data?.cart ?? event?.detail?.resource;
    if (cart?.items) this.#cart = cart;
    else this.#refreshCart();
  };

  async #refreshCart() {
    try {
      const res = await fetch('/cart.js');
      if (res.ok) this.#cart = await res.json();
    } catch {
      /* leave last snapshot in place */
    }
    return this.#cart;
  }

  /**
   * Boot-time cleanup: if the cart holds gift items but no paid item to justify
   * them, remove the gifts. No-op when there are no stuck gifts.
   */
  async #sweepStuckGifts() {
    const cart = this.#cart;
    if (!cart?.items?.length) return;
    const hasGift = cart.items.some(isGiftItem);
    if (!hasGift) return;
    // Only a stuck-gift situation when EVERY item is a gift. When paid items are
    // present, gifts are assumed legitimate and left to the gift manager (if on).
    if (!hasOnlyGifts(cart)) return;
    await this.#removeAllGifts(cart);
  }

  /** @param {MouseEvent} event */
  #onCheckoutClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest(CHECKOUT_SELECTOR);
    if (!trigger) return;

    const cart = this.#cart;
    // No snapshot yet, or cart has a paid item → allow checkout to proceed.
    if (!cart || !cart.items) return;
    if (!hasOnlyGifts(cart) && cart.items.length > 0) return;

    // Cart is empty or gift-only → block the checkout and clean up.
    event.preventDefault();
    event.stopImmediatePropagation();

    this.#notify();
    if (hasOnlyGifts(cart)) this.#removeAllGifts(cart);
  };

  /**
   * Remove every gift line from the cart, then broadcast a cart update so the
   * drawer/cart page re-render.
   * @param {{ items?: any[] }} cart
   */
  async #removeAllGifts(cart) {
    if (this.#sweeping) return;
    this.#sweeping = true;
    try {
      // Reverse line order so earlier indices stay valid as we remove.
      const giftLines = (cart.items || [])
        .map((item, idx) => (isGiftItem(item) ? idx + 1 : null))
        .filter((/** @type {number | null} */ l) => l !== null)
        .sort((/** @type {any} */ a, /** @type {any} */ b) => b - a);
      if (giftLines.length === 0) return;

      const mutationToken = nextCartMutation();
      const sections = cartItemsSectionIds();
      /** @type {any} */
      let lastResponse = null;
      for (let i = 0; i < giftLines.length; i++) {
        const line = giftLines[i];
        const isLastRequest = i === giftLines.length - 1;
        /** @type {Record<string, any>} */
        const body = { line };
        // Only the final request needs the rendered sections — intermediate
        // responses are superseded, so rendering them is wasted server work.
        if (isLastRequest) {
          body.sections = sections;
          body.sections_url = window.location.pathname;
        }

        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        lastResponse = await res.json();
        if (lastResponse?.status || lastResponse?.errors) break;
      }

      const finalCart = lastResponse?.items ? lastResponse : await this.#refreshCart();
      this.#cart = finalCart;
      document.dispatchEvent(
        new CartUpdateEvent(finalCart || {}, SOURCE_ID, {
          source: SOURCE_ID,
          itemCount: finalCart?.item_count ?? 0,
          mutationToken,
          sections: lastResponse?.sections,
        })
      );
    } catch (error) {
      console.error('[cart-gift-guard]', error);
    } finally {
      this.#sweeping = false;
    }
  }

  #notify() {
    // Localized copy is injected by cart-features-bootstrap.liquid; fall back to
    // pt-BR (primary market) if the global is absent.
    const message =
      /** @type {any} */ (window).__cartGiftGuardMessage || 'Adicione um produto para finalizar a compra.';
    document.dispatchEvent(new CartErrorEvent(SOURCE_ID, message, 'Carrinho contém apenas brinde', {}));
  }
}

if (!(/** @type {any} */ (window).__cartGiftGuard)) {
  const guard = new CartGiftGuard();
  /** @type {any} */ (window).__cartGiftGuard = guard;
  guard.init();
}
